import { formatDate } from "@/lib/utils";
import { normalizeSerial } from "@/lib/posSerialUtils";
import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Search,
  Check,
  X,
  Layers,
  ScanBarcode,
  Package,
  Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import Modal from "@/components/ui/modal";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent } from "@/components/ui/card";

const InventorySelectorModal = ({
  show,
  onHide,
  type, // "standard" (batch) or "serial"
  availableItems = [], // availableBatches or availableSerials
  selectedItems = [], // currentItemConfig.serial_numbers or [currentItemConfig.inventory_batch_id]
  onSelect,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [localSelectedItems, setLocalSelectedItems] = useState([]);

  React.useEffect(() => {
    if (show) {
      setLocalSelectedItems(selectedItems || []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]); // Only re-run when `show` changes

  const uniqueAvailableItems = useMemo(() => {
    if (type !== "serial") return availableItems;
    const seen = new Set();
    return (availableItems || []).filter((item) => {
      const sn = normalizeSerial(item);
      const lower = sn.toLowerCase();
      if (!lower || seen.has(lower)) return false;
      seen.add(lower);
      return true;
    });
  }, [availableItems, type]);

  const filteredItems = useMemo(() => {
    return uniqueAvailableItems.filter((item) => {
      const searchStr = (
        type === "standard"
          ? item.batch_number || ""
          : normalizeSerial(item)
      ).toLowerCase();
      return searchStr.includes(searchTerm.toLowerCase());
    });
  }, [uniqueAvailableItems, searchTerm, type]);

  const handleToggle = (itemValue) => {
    if (type === "standard") {
      onSelect(itemValue);
    } else {
      const valStr = normalizeSerial(itemValue);
      let newSerials = [...localSelectedItems];
      const idx = newSerials.findIndex(
        (s) => normalizeSerial(s).toLowerCase() === valStr.toLowerCase()
      );
      if (idx >= 0) {
        newSerials.splice(idx, 1);
      } else {
        newSerials.push(valStr);
      }
      setLocalSelectedItems(newSerials);
    }
  };

  return (
    <Modal
      open={show}
      onOpenChange={(open) => !open && onHide()}
      title={type === "standard" ? "Select Batch" : "Select Serial Numbers"}
      icon={type === "standard" ? Layers : ScanBarcode}
      maxWidth="max-w-2xl"
      footer={
        <div className="flex w-full justify-between items-center">
          <div className="text-sm">
            {type === "serial" && (
              <span
                className={
                  localSelectedItems.length > 0
                    ? "text-emerald-600 font-bold"
                    : "text-blue-600"
                }
              >
                Selected: {localSelectedItems.length}
              </span>
            )}
          </div>
          <Button
            onClick={() => {
              if (type === "serial") {
                const uniqueSelected = Array.from(
                  new Set(
                    localSelectedItems.map(normalizeSerial).filter(Boolean),
                  ),
                );
                onSelect(uniqueSelected);
              } else {
                onHide();
              }
            }}
          >
            Done
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={`Search ${type === "standard" ? "batch" : "serial"}...`}
            className="pl-9"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <ScrollArea className="h-[400px] pr-4">
          <div
            className={`grid gap-2 ${type === "serial" ? "grid-cols-2" : "grid-cols-1"}`}
          >
            {filteredItems.map((item) => {
              const itemValue =
                type === "standard" ? item.id : normalizeSerial(item);
              const isSelected =
                type === "standard"
                  ? selectedItems[0] === itemValue
                  : localSelectedItems.some(
                    (s) =>
                      normalizeSerial(s).toLowerCase() ===
                      itemValue.toLowerCase(),
                  );

              const canSell =
                type === "standard"
                  ? true
                  : (item.status_info?.can_sell ?? item.is_available ?? true);

              return (
                <Card
                  key={itemValue}
                  className={`transition-all border-2 ${!canSell
                      ? "opacity-50 cursor-not-allowed bg-muted/50 border-border"
                      : isSelected
                        ? "border-primary bg-primary/5 cursor-pointer"
                        : "border-border hover:border-primary/30 cursor-pointer"
                    }`}
                  onClick={() => canSell && handleToggle(itemValue)}
                >
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div
                        className={`p-2 rounded-full ${!canSell
                            ? "bg-muted text-muted-foreground/50"
                            : isSelected
                              ? "bg-primary text-white"
                              : "bg-muted text-muted-foreground"
                          }`}
                      >
                        {type === "standard" ? (
                          <Package size={16} />
                        ) : (
                          <ScanBarcode size={16} />
                        )}
                      </div>
                      <div className="space-y-0.5 min-w-0 flex-1">
                        <p className="font-bold text-sm truncate">
                          {type === "standard"
                            ? item.batch_number
                            : normalizeSerial(item)}
                        </p>
                        {type === "standard" ? (
                          <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
                            {item.expiry_date && (
                              <span className="flex items-center gap-1">
                                <Calendar size={10} />
                                Exp:{" "}
                                {formatDate(item.expiry_date,)}
                              </span>
                            )}
                            <Badge variant="outline" className="h-4 px-1">
                              Stock: {item.quantity}
                            </Badge>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            {item.status_info?.label && (
                              <Badge
                                variant={
                                  item.status_info.variant || "secondary"
                                }
                                className="text-[9px] h-4 px-1"
                              >
                                {item.status_info.label}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    {type === "serial" ? (
                      <div onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={isSelected}
                          disabled={!canSell}
                          onCheckedChange={() => canSell && handleToggle(itemValue)}
                        />
                      </div>
                    ) : (
                      isSelected && <Check className="h-4 w-4 text-primary" />
                    )}
                  </CardContent>
                </Card>
              );
            })}
            {filteredItems.length === 0 && (
              <div className="py-20 text-center text-muted-foreground">
                <p>No results found for &quot;{searchTerm}&quot;</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </Modal>
  );
};

InventorySelectorModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  type: PropTypes.oneOf(["standard", "serial", "none"]).isRequired,
  availableItems: PropTypes.array,
  selectedItems: PropTypes.array,
  onSelect: PropTypes.func.isRequired,
  quantity: PropTypes.number,
};

export default InventorySelectorModal;
